import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFileDownloadUrl } from "../../../../../api/firebase/firestoreService";
import { CmmDocType } from "../../../../../enums/cmmDocType";
import {
  fetchSingleOrderDocs,
  resetDocuments,
} from "../../../../../redux/slice/nycbsPharmaExternal/nycbsDocumentsSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";

interface UsePrescriptionImageDataProps {
  rowId?: string | number;
}

interface UsePrescriptionImageDataReturn {
  prescriptionImageUrls: string[];
  loading: boolean;
  isLoadingUrls: boolean;
  error: string;
  loadedImagesCount: number;
  handleImageLoad: () => void;
}

export const usePrescriptionImageData = ({
  rowId,
}: UsePrescriptionImageDataProps): UsePrescriptionImageDataReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: singleOrderDocs, loading } = useSelector(
    (state: RootState) => state.nycbsDocuments,
  );
  const [prescriptionImageUrls, setPrescriptionImageUrls] = useState<string[]>(
    [],
  );
  const [isLoadingUrls, setIsLoadingUrls] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);

  // Effect to fetch single order documents when rowId changes
  useEffect(() => {
    if (rowId) {
      dispatch(fetchSingleOrderDocs(String(rowId)));
    }

    return () => {
      dispatch(resetDocuments());
    };
  }, [rowId, dispatch]);

  // Effect to fetch prescription image URLs when documents are received
  useEffect(() => {
    const fetchPrescriptionImageUrls = async () => {
      if (!singleOrderDocs || singleOrderDocs.length === 0) return;

      const prescriptionDocs = singleOrderDocs.filter(
        (doc) => doc.document_type === CmmDocType.PRESCRIPTION,
      );

      if (prescriptionDocs.length > 0) {
        setIsLoadingUrls(true);
        setError("");
        setLoadedImagesCount(0);
        try {
          const urls = await Promise.all(
            prescriptionDocs.map((doc) =>
              getFileDownloadUrl(doc.file_path ?? ""),
            ),
          );
          const validUrls = urls.filter((url) => url !== null) as string[];
          if (validUrls.length > 0) {
            setPrescriptionImageUrls(validUrls);
          } else {
            setError("Failed to load prescription images");
          }
        } catch (err) {
          console.error("Error fetching prescription image URLs:", err);
          setError("Error loading prescription images");
        } finally {
          setIsLoadingUrls(false);
        }
      }
    };

    fetchPrescriptionImageUrls();
  }, [singleOrderDocs]);

  const handleImageLoad = () => {
    setLoadedImagesCount((prev) => prev + 1);
  };

  return {
    prescriptionImageUrls,
    loading,
    isLoadingUrls,
    error,
    loadedImagesCount,
    handleImageLoad,
  };
};
